import React, { useState, useEffect, useReducer, useCallback, useContext, useMemo } from 'react';

// assets
// material ui assets
import Skeleton from '@material-ui/lab/Skeleton';
import { ScrollContext } from 'App';
import { ADD_FILTER, REMOVE_FILTER } from 'actions/actionTypes';
import { setTeamAction, resetAllAction, setFiltersAction } from 'actions/globalFilters';
import TreeView, {
  getGoalsForTeam,
  getObjectives,
  buildFiltersForTeam,
  loadChildrenData,
  buildObjectivesData,
  getDisplayNameForTeam,
} from 'components/TreeView';
// project data and models
import TeamModel from 'models/team';
// selectors
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useLocation, withRouter } from 'react-router-dom';
import CustomBackdrop from 'screens/Team/CustomBackdrop';
import RefreshButton from 'screens/Team/SectionHeader/RefreshButton';
import TeamPageFilters, {
  initialFiltersState,
  filtersReducersWithClearAll,
  RESET_ALL_FILTERS,
} from 'screens/Team/TeamPageFilters';
import buildTeamPageFilters from 'screens/Team/TeamPageFilters/buildTeamPageFilters';
// helpers
import { getSelectedFiltersCountFromMap, dateParser } from 'screens/Team/TeamPageFilters/helper';
import { getStartTSEndTSFromDateFilters } from 'screens/Team/TeamPageFilters/helper';
// components
import TeamTasksCardView from 'screens/Team/TeamTasksCardView';
import TeamTasksListView from 'screens/Team/TeamTasksListView';
import orderByReducer, { orderByInitialState } from 'screens/Team/TeamTasksListView/orderByReducer';
import taskReducer, { ADD_TASKS } from 'screens/Team/TeamTasksListView/taskReducer';
import ViewTypes from 'screens/Team/ViewTypes';
import { getEnabledTaskTypeMenuItems, findAncestor } from 'screens/Team/helper';
import { Container } from 'screens/Team/styles';
import SectionHeading from 'screens/meshIn/SectionHeading';
import { selectTeamPageGlobalFilters } from 'selectors/globalFilters';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
// styles
import { StyledTypography, VerticallyCenterContainer, StyledButton } from 'styles/common';
import { checkModuleEnabled } from 'utils/helpers/moduleAccess';

import {
  TEAM_DURATION_VALUES,
  TEAM_TASKS_VIEW_TYPES,
  SEARCH_TAG_TYPES,
  TASK_TYPES_2,
  TEAM_TYPE,
  DEFAULT_ROWS_PER_PAGE,
} from 'constants/ProjectConstants';
import { getQueryParams } from 'constants/helper';
import { localStorageKeys } from 'constants/localStorageKeys';
// import logger from 'use-reducer-logger';
import { sendTeamHubEvents, TEAM_HUB_EVENTS } from 'constants/mixpanelHelper';
import { routesForTracking } from 'constants/routesForTracking';

import { getStartEndTimeFromDurationKey } from 'services/helpers/task';
import { showInfoSnackbar } from 'services/snackbar';
import { getTeamTasksForListView, getTeamTasksStats } from 'services/task';

export function getTeamCountHeading(teamCount, viewType = '') {
  if (viewType === TEAM_TASKS_VIEW_TYPES.goalTree) return 'Goals Tree';
  if (teamCount <= 0) return 'Team Members';
  if (teamCount === 1) return '1 Team Member';
  return `${teamCount} Team Members`;
}

const UPDATE_TASKS_DATA_CHANGE = 'UPDATE_TASKS_DATA_CHANGE';
const UPDATE_TASKS_RELOAD = 'UPDATE_TASKS_RELOAD';

function tasksDataChangeReducer(state, action) {
  switch (action.type) {
    case UPDATE_TASKS_DATA_CHANGE:
      return { ...state, [action.data.type]: action.data.value };
    // case UPDATE_TASKS_RELOAD:
    //   const newState = { ...state };
    //   newState.reload[action.data.type] = action.data.value;
    //   return newState;
    default:
      return state;
  }
}

function useScrollToTopState(pathname) {
  const { current: scrollParentRef } = useContext(ScrollContext);

  useEffect(() => {
    // when you come on team page, make sure that you scroll to the top
    if (scrollParentRef) {
      scrollParentRef.scrollTo(0, 0);
    }
  }, [pathname, scrollParentRef]);
}

export function useViewTypeState(selectedTaskType) {
  const localStorageViewType = getLocalStorageViewType(selectedTaskType);
  const [teamTasksViewType, setTeamTasksViewType] = useState(
    localStorageViewType || getDefaultViewType(selectedTaskType)
  );
  function handleTeamViewTypeChange(event, viewType) {
    if (!viewType) return;

    sendTeamHubEvents({
      eventName: TEAM_HUB_EVENTS.toggledViewType,
      page: {
        pageName: routesForTracking.team.pageName,
        flow: 'Activity',
      },
      view: viewType,
      taskType: selectedTaskType,
    });

    setTeamTasksViewType(viewType);
    setLocalStorageViewType(selectedTaskType, viewType);
  }
  return { teamTasksViewType, setTeamTasksViewType, handleTeamViewTypeChange };
}

export const getLocalStorageFilters = (type) => {
  const key =
    type === TASK_TYPES_2.goal ? localStorageKeys.teamPageGoalsFilters : localStorageKeys.teamPageTasksFilters;
  const val = localStorage.getItem(key);
  return (val && JSON.parse(val, dateParser)) || null;
};

export const setLocalStorageFilters = (type, filters) => {
  const key =
    type === TASK_TYPES_2.goal ? localStorageKeys.teamPageGoalsFilters : localStorageKeys.teamPageTasksFilters;
  localStorage.setItem(key, JSON.stringify(filters));
};

export const getLocalStorageViewType = (type) => {
  const key =
    type === TASK_TYPES_2.goal ? localStorageKeys.teamPageGoalsViewType : localStorageKeys.teamPageTasksViewType;
  const val = localStorage.getItem(key);
  return val || null;
};

export const setLocalStorageViewType = (type, viewType) => {
  const key =
    type === TASK_TYPES_2.goal ? localStorageKeys.teamPageGoalsViewType : localStorageKeys.teamPageTasksViewType;
  localStorage.setItem(key, viewType);
};

export const getLocalStorageStatsFilters = (type) => {
  const key =
    type === TASK_TYPES_2.goal
      ? localStorageKeys.teamPageGoalsStatsFilters
      : localStorageKeys.teamPageTasksStatsFilters;
  const val = localStorage.getItem(key);

  return (val && JSON.parse(val)) || null;
};

const defaultGoalStats = [
  {
    type: 'STATUS',
    value: 'ON_TRACK',
    displayValue: 'On-Track',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'OFF_TRACK',
    displayValue: 'Off-Track',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'AT_RISK',
    displayValue: 'At-Risk',
    operator: 'IN',
  },
];

const defaultTaskStats = [
  {
    type: 'STATUS',
    value: 'ONGOING',
    displayValue: 'On-going',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'DELAYED',
    displayValue: 'Delayed',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'UNPLANNED',
    displayValue: 'Unplanned',
    operator: 'IN',
  },
];

export const getDefaultLocalStorageStatsFilters = (type) => {
  const val = getLocalStorageStatsFilters(type);
  if (val) {
    return val;
  }

  if (type === TASK_TYPES_2.goal) {
    setLocalStorageStatsFilters(type, defaultGoalStats);
    return defaultGoalStats;
  }
  setLocalStorageStatsFilters(type, defaultTaskStats);
  return defaultTaskStats;
};

export const setLocalStorageStatsFilters = (type, filters) => {
  const key =
    type === TASK_TYPES_2.goal
      ? localStorageKeys.teamPageGoalsStatsFilters
      : localStorageKeys.teamPageTasksStatsFilters;
  localStorage.setItem(key, JSON.stringify(filters));
};

export function useTeamPageState(match) {
  const location = useLocation();
  const [teamCount, setTeamCount] = useState(0);
  // const [performanceStats, setPerformanceStats] = useState({ reliabilityScore: 0, growth: 0 });
  const [team, setTeam] = useState(null);
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const personalTeam = loggedInEmployee.teams.filter((team) => team.teamType === TEAM_TYPE.personal)[0];
  const durationMenuItems = TEAM_DURATION_VALUES;
  const [selectedDurationId, setSelectedDurationValue] = useState('LASTYEAR');
  let selectedTaskTypeFromLs = localStorage.getItem(localStorageKeys.selectedTaskType);
  if (!checkModuleEnabled(selectedTaskTypeFromLs)) {
    // module not enabled, so not valid anymore
    selectedTaskTypeFromLs = null;
    localStorage.removeItem(localStorageKeys.selectedTaskType);
  }
  const [selectedTaskType, setSelectedTaskType] = useState(
    selectedTaskTypeFromLs || getEnabledTaskTypeMenuItems()[0].value
  );
  const isGoalOptionSelected = selectedTaskType === TASK_TYPES_2.goal;
  const { teamTasksViewType, setTeamTasksViewType, handleTeamViewTypeChange } = useViewTypeState(selectedTaskType);

  // const [teamTasksViewType, setTeamTasksViewType] = useState(getDefaultViewType(selectedTaskType));
  const dispatch = useDispatch();
  const [tasksLoading, updateTasksLoading] = useState(false);
  const activeFilters = useSelector(selectTeamPageGlobalFilters, shallowEqual).filters;

  // const [activeFilters, activeFiltersDispatch] = useReducer(filtersReducer, []);
  const [tasksDataChanged, tasksDataChangeDispatch] = useReducer(tasksDataChangeReducer, {
    [TEAM_TASKS_VIEW_TYPES.card]: false,
    [TEAM_TASKS_VIEW_TYPES.list]: false,
  });
  useScrollToTopState(location.pathname);
  const [taskTags, setTaskTags] = useState(null);
  const [goalTags, setGoalTags] = useState(null);
  const [refreshData, setRefreshData] = useState(false);
  const addStatsFilter = (type, value, displayValue, operator) => {
    dispatch({
      type: ADD_FILTER,
      data: {
        type,
        value,
        displayValue,
        operator: operator || 'IN',
      },
    });
    // save to local storage too
    const currentStatsFilters = getLocalStorageStatsFilters(selectedTaskType) || [];
    setLocalStorageStatsFilters(selectedTaskType, [
      ...currentStatsFilters,
      {
        type,
        value,
        displayValue,
        operator: operator || 'IN',
      },
    ]);
  };

  const updateIsTasksDataChanged = useCallback((type, value) => {
    tasksDataChangeDispatch({ type: UPDATE_TASKS_DATA_CHANGE, data: { type, value } });
  }, []);
  const updateIsCardViewDataChanged = useCallback(
    (value) => {
      updateIsTasksDataChanged(TEAM_TASKS_VIEW_TYPES.card, value);
    },
    [updateIsTasksDataChanged]
  );

  const removeStatsFilter = (type, value, displayValue, operator) => {
    dispatch({
      type: REMOVE_FILTER,
      data: {
        type,
        value,
        operator: operator || 'IN',
      },
    });
    let currentStatsFilters = getLocalStorageStatsFilters(selectedTaskType) || [];
    currentStatsFilters = currentStatsFilters.filter((item) => item.value !== value);
    setLocalStorageStatsFilters(selectedTaskType, [...currentStatsFilters]);
  };
  const [newFiltersState, newFiltersDispatch] = useReducer(
    // logger(filtersReducersWithClearAll),
    filtersReducersWithClearAll,
    initialFiltersState
  );

  useEffect(() => {
    const localStorageFilters = getLocalStorageFilters(selectedTaskType);
    const filters = localStorageFilters || initialFiltersState;
    // removing initial state of type = "TASKS" when GOALS is selectedTaskType
    if (isGoalOptionSelected) {
      filters.taskTypeFilters = {};
    }

    newFiltersDispatch({ type: RESET_ALL_FILTERS, data: filters });
  }, [selectedTaskType]);

  return {
    teamCount,
    setTeamCount,
    // performanceStats,
    // setPerformanceStats,
    team,
    setTeam,
    personalTeam,
    selectedDurationId,
    setSelectedDurationValue,
    setSelectedTaskType,
    isGoalOptionSelected,
    teamTasksViewType,
    dispatch,
    tasksLoading,
    updateTasksLoading,
    activeFilters,
    tasksDataChanged,
    taskTags,
    setTaskTags,
    goalTags,
    setGoalTags,
    refreshData,
    setRefreshData,
    setTeamTasksViewType,
    addStatsFilter,
    updateIsTasksDataChanged,
    updateIsCardViewDataChanged,
    removeStatsFilter,
    handleTeamViewTypeChange,
    selectedTaskType,
    loggedInEmployee,
    durationMenuItems,
    activeFiltersDispatch: dispatch,
    newFiltersState,
    newFiltersDispatch,
  };
}

export function getDefaultViewType(selectedTaskType) {
  return selectedTaskType === TASK_TYPES_2.goal ? TEAM_TASKS_VIEW_TYPES.list : TEAM_TASKS_VIEW_TYPES.card;
}

function getCompletedOnDurationFromFilters(newFiltersState) {
  const selectedCompletedFiltersCount = getSelectedFiltersCountFromMap(newFiltersState.completedOnFilters);
  return selectedCompletedFiltersCount > 0
    ? getStartTSEndTSFromDateFilters(newFiltersState.completedOnFilters)
    : getStartEndTimeFromDurationKey('LASTYEAR');
}

function MSTeamHubView({ type, history, match, location }) {
  const loggedInEmployeeData = useSelector(selectLoggedInEmployeeData);
  const employeeTeams = loggedInEmployeeData.teams || [{ id: '-1' }];

  const queryString = location.search;
  const paramsTeamId = match.params.teamId || employeeTeams[0].id;
  const dispatch = useDispatch();

  const {
    teamCount,
    setTeamCount,
    team,
    setTeam,
    personalTeam,
    setSelectedDurationValue,
    setSelectedTaskType,
    isGoalOptionSelected,
    teamTasksViewType,
    tasksLoading,
    updateTasksLoading,
    activeFilters,
    tasksDataChanged,
    setTaskTags,
    setGoalTags,
    setTeamTasksViewType,
    refreshData,
    setRefreshData,
    addStatsFilter,
    updateIsTasksDataChanged,
    updateIsCardViewDataChanged,
    removeStatsFilter,
    // resetGlobalApiFilters,
    handleTeamViewTypeChange,
    selectedTaskType,
    loggedInEmployee,
    activeFiltersDispatch,
    newFiltersState,
    newFiltersDispatch,
  } = useTeamPageState(match);

  const [duration, setDuration] = useState(getCompletedOnDurationFromFilters(newFiltersState));
  const [isOpenCreateGoalPanel, setIsOpenCreateGoalPanel] = useState(false);
  const [isOpenCreateGoalTreeViewPanel, setIsOpenCreateGoalTreeViewPanel] = useState(false);
  const [isCreateNewTaskInProgress, setIsCreateNewTaskInProgress] = useState(false);
  const [statsValues, setStatsValues] = useState({
    count: 0,
    completed: 0,
    ongoing: 0,
    delayed: 0,
    unplanned: 0,
    highProgress: 0,
    mediumProgress: 0,
    lowProgress: 0,
    onTrackProgress: 0,
    offTrackProgress: 0,
    atRiskProgress: 0,
    closedProgress: 0,
  });
  // had to keep new filter logic separate from the old, so that it works on mobile as well
  // TODO: remove old filter logic and only use this new one
  useEffect(() => {
    const newActiveFilters = buildTeamPageFilters(newFiltersState, isGoalOptionSelected);
    setDuration((duration) => {
      const newDuration = getCompletedOnDurationFromFilters(newFiltersState);
      return JSON.stringify(duration) !== JSON.stringify(newDuration) ? newDuration : duration;
    });
    const localStorageStatsFilters = getDefaultLocalStorageStatsFilters(selectedTaskType) || [];
    activeFiltersDispatch(setFiltersAction([...newActiveFilters, ...localStorageStatsFilters]));
  }, [JSON.stringify(newFiltersState)]);

  const updateIsTableDataChanged = useCallback(
    (value) => {
      updateIsTasksDataChanged(TEAM_TASKS_VIEW_TYPES.list, value);
    },
    [updateIsTasksDataChanged]
  );

  useEffect(() => {
    const teamId = paramsTeamId;
    if (!teamId) return;
    const { teamName } = getQueryParams(queryString);

    dispatch(setTeamAction(teamId)).then(() => {
      setTeam(new TeamModel({ id: teamId, name: teamName }));
    });
  }, [paramsTeamId]);

  useEffect(
    () => () => {
      dispatch(resetAllAction());
    },
    []
  );

  useEffect(() => {
    const { employeeFilters, ...otherTaskFilters } = newFiltersState;
    const { primaryPodFilters, ...otherGoalFilters } = otherTaskFilters;

    if (selectedTaskType === 'TASK') {
      setLocalStorageFilters(selectedTaskType, otherTaskFilters);
    } else {
      setLocalStorageFilters(selectedTaskType, otherGoalFilters);
    }
  }, [selectedTaskType, newFiltersState]);

  const onTaskTypeChange = (e, value) => {
    if (value) localStorage.setItem(localStorageKeys.selectedTaskType, value);
    setSelectedTaskType(value || getEnabledTaskTypeMenuItems()[0].value);

    const teamTasksViewType = TEAM_TASKS_VIEW_TYPES.list;
    setTeamTasksViewType(teamTasksViewType);

    sendTeamHubEvents({
      eventName: TEAM_HUB_EVENTS.toggledTaskType,
      page: {
        pageName: routesForTracking.team.pageName,
        flow: 'Activity',
      },
      taskType: value,
      view: teamTasksViewType,
    });

    const localStorageFilters = getLocalStorageFilters(value);
    const localStorageStatsFilters = getDefaultLocalStorageStatsFilters(value) || [];
    if (localStorageFilters) {
      dispatch(
        setFiltersAction([
          ...buildTeamPageFilters(localStorageFilters, value === TASK_TYPES_2.goal),
          ...localStorageStatsFilters,
        ])
      );
    } else {
      dispatch(setFiltersAction([...localStorageStatsFilters]));
    }
  };

  useEffect(() => {
    onTaskTypeChange(null, type);
  }, []);

  // sorting logic
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [loading, setLoading] = useState(false);
  const [totalTasks, setTotalTasks] = useState(0);
  const [tasks, tasksDispatch] = useReducer(taskReducer, []);
  const [goals, goalsDispatch] = useReducer(taskReducer, []);

  const [{ field: orderBy, type: order }, orderByDispatch] = useReducer(orderByReducer, orderByInitialState);

  // getdata for sorting

  const getData = (resetPage) => {
    // if (loading) {
    //   return false;
    // }
    setLoading(true);
    updateTasksLoading(true);
    if (resetPage) {
      setPage(0);
    }

    getTeamTasksStats({
      tagId: team.id,
      tagType: SEARCH_TAG_TYPES.team,
      // durationKey,
      startTS: duration.startTS,
      endTS: duration.endTS,
      filters: activeFilters,
      type: selectedTaskType,
    })
      .then((res) => {
        setTotalTasks(res.entity.count);
        getTasks(resetPage);
      })
      .catch((err) => {
        if (selectedTaskType === TASK_TYPES_2.task) {
          tasksDispatch({ type: ADD_TASKS, data: { tasks: [] } });
        } else {
          goalsDispatch({ type: ADD_TASKS, data: { tasks: [] } });
        }
        setLoading(false);
        updateTasksLoading(false);
      });
  };

  const getTasks = (resetPage) => {
    setLoading(true);
    updateTasksLoading(true);

    getTeamTasksForListView({
      tagId: team.id,
      tagType: SEARCH_TAG_TYPES.team,
      // durationKey,
      startTS: duration.startTS,
      endTS: duration.endTS,
      limit: rowsPerPage,
      offset: resetPage ? 0 : page * rowsPerPage,
      orderByParam: orderBy,
      order,
      filters: activeFilters,
      type: selectedTaskType,
    })
      .then((tasks) => {
        if (selectedTaskType === TASK_TYPES_2.task) {
          tasksDispatch({ type: ADD_TASKS, data: { tasks } });
        } else {
          goalsDispatch({ type: ADD_TASKS, data: { tasks } });
        }
      })
      .catch((err) => {
        if (selectedTaskType === TASK_TYPES_2.task) {
          tasksDispatch({ type: ADD_TASKS, data: { tasks: [] } });
        } else {
          goalsDispatch({ type: ADD_TASKS, data: { tasks: [] } });
        }
      })
      .finally(() => {
        updateTasksLoading(false);
        setLoading(false);
      });
  };

  const cardViewStyles = useMemo(
    () => ({
      display: teamTasksViewType === TEAM_TASKS_VIEW_TYPES.card ? 'flex' : 'none',
    }),
    [teamTasksViewType]
  );
  const listviewStyles = useMemo(
    () => ({
      display: teamTasksViewType === TEAM_TASKS_VIEW_TYPES.list ? 'block' : 'none',
    }),
    [teamTasksViewType]
  );

  const addNewGoal = () => {
    if (teamTasksViewType === TEAM_TASKS_VIEW_TYPES.list) {
      setIsOpenCreateGoalPanel(true);
    } else if (teamTasksViewType === TEAM_TASKS_VIEW_TYPES.goalTree) {
      setIsOpenCreateGoalTreeViewPanel(true);
    } else {
      const currentUserCreateGoalButton = document.getElementsByClassName(`create-goal-${loggedInEmployee.id}`)[0];
      // logged in user mesh list is not shown
      if (!currentUserCreateGoalButton) {
        dispatch(
          showInfoSnackbar(
            `Your goals list is not shown as per applied filters. To create a goal for other person, use the Create New button on their list.`,
            4000
          )
        );
        return;
      }
      const meshlistAncestor = findAncestor(currentUserCreateGoalButton, 'meshlist');
      meshlistAncestor.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
      currentUserCreateGoalButton.click();
    }
  };

  const addNewTask = () => {
    if (teamTasksViewType === TEAM_TASKS_VIEW_TYPES.list) {
      setIsCreateNewTaskInProgress(true);
    } else {
      const currentUserCreateTaskButton = document.getElementsByClassName(`create-task-${loggedInEmployee.id}`)[0];
      // logged in user mesh list is not shown
      if (!currentUserCreateTaskButton) {
        dispatch(
          showInfoSnackbar(
            `Your tasks list is not shown as per applied filters. To create a task for other person, use the Create New button on their list.`,
            4000
          )
        );
        return;
      }
      const meshlistAncestor = findAncestor(currentUserCreateTaskButton, 'meshlist');
      meshlistAncestor.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
      currentUserCreateTaskButton.click();
    }
  };

  const refresh = () => {
    setRefreshData(true);
    setTimeout(() => {
      setRefreshData(false);
    }, 100);
  };

  const showListView = useCallback(() => {
    setTeamTasksViewType(TEAM_TASKS_VIEW_TYPES.list);
  }, [setTeamTasksViewType]);

  const memoizedFiltersState = useMemo(() => newFiltersState, [newFiltersState]);

  return team && team.id ? (
    <Container style={{ height: 'auto' }} className="teamPageContainer">
      {/* <MainContent> */}
      <TeamPageFilters
        teamId={team.id}
        filtersDispatch={activeFiltersDispatch}
        newFiltersState={memoizedFiltersState}
        newFiltersDispatch={newFiltersDispatch}
        selectedTaskType={selectedTaskType}
        orderByDispatch={orderByDispatch}
        orderBy={orderBy}
        order={order}
        teamTasksViewType={teamTasksViewType}
      />
      <SectionHeading
        HeadingComponent={
          <VerticallyCenterContainer>
            <SectionHeading.Heading
              display="inline"
              variant="h6"
              color="textPrimary"
              weight={600}
              heading={`${type === 'GOAL' ? 'Goals' : 'Initiatives'}`}
            />
            <RefreshButton onClick={refresh} isDisabled={tasksLoading} />
          </VerticallyCenterContainer>
        }
        actionComponent={
          <ViewTypes teamTasksViewType={teamTasksViewType} handleTeamViewTypeChange={handleTeamViewTypeChange}>
            <CreateButton
              onClick={isGoalOptionSelected ? addNewGoal : addNewTask}
              customColor="white"
              fontColor="primary"
              variant="outlined"
            >
              <StyledTypography weight={600} variant="body2" lineheight="24px" texttransform="capitalize">
                Create new {isGoalOptionSelected ? 'goal' : 'initiative'}
              </StyledTypography>
            </CreateButton>

            {/* <ViewTypes.ViewTypeDisplay /> */}
            {/* <ViewTypes.Toggles>
                <Toggles.Toggle
                  key={1}
                  value={TEAM_TASKS_VIEW_TYPES.card}
                  icon={
                    <StyledIcon size="tiny">
                      <ViewCarouselRoundedIcon />
                    </StyledIcon>
                  }
                />
                <Toggles.Toggle
                  key={2}
                  value={TEAM_TASKS_VIEW_TYPES.list}
                  icon={
                    <StyledIcon size="tiny">
                      <FormatListNumberedRoundedIcon />
                    </StyledIcon>
                  }
                  isLast={!isGoalOptionSelected}
                />
                {isGoalOptionSelected && (
                  <Toggles.Toggle
                    key={3}
                    value={TEAM_TASKS_VIEW_TYPES.goalTree}
                    icon={
                      <StyledIcon size="tiny">
                        <HierarchyIcon />
                      </StyledIcon>
                    }
                    isLast={isGoalOptionSelected}
                  />
                )}
              </ViewTypes.Toggles> */}
          </ViewTypes>
        }
      />

      <ViewsContainer>
        {TEAM_TASKS_VIEW_TYPES.goalTree === teamTasksViewType && (
          <TreeView
            getInitialData={getObjectives.bind(null, { parentTagId: null })}
            getChildrenData={(parentTagId) =>
              loadChildrenData(
                getObjectives.bind(null, { parentTagId }),
                getGoalsForTeam(team.id).bind(null, buildFiltersForTeam(parentTagId))
              )
            }
            isOpenCreateGoalPanel={isOpenCreateGoalTreeViewPanel}
            setIsOpenCreateGoalPanel={setIsOpenCreateGoalTreeViewPanel}
            employee={loggedInEmployee}
            buildInitialTreeData={buildObjectivesData(getDisplayNameForTeam)}
          />
        )}
        {/* <TeamViewTypesContainer></TeamViewTypesContainer> */}
        <TeamTasksListView
          tagId={team.id}
          tagType={SEARCH_TAG_TYPES.team}
          style={listviewStyles}
          activeFilters={activeFilters}
          isSectionVisible={TEAM_TASKS_VIEW_TYPES.list === teamTasksViewType}
          updateTasksLoading={updateTasksLoading}
          updateIsTasksDataChanged={updateIsTableDataChanged}
          hasCardViewDataChanged={tasksDataChanged[TEAM_TASKS_VIEW_TYPES.card]}
          refreshData={refreshData}
          employee={loggedInEmployee}
          selectedTeam={team}
          type={selectedTaskType}
          isOpenCreateGoalPanel={isOpenCreateGoalPanel}
          setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
          isCreateNewTaskInProgress={isCreateNewTaskInProgress}
          setIsCreateNewTaskInProgress={setIsCreateNewTaskInProgress}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          orderBy={orderBy}
          order={order}
          orderByDispatch={orderByDispatch}
          loading={loading}
          setLoading={setLoading}
          totalTasks={totalTasks}
          setTotalTasks={setTotalTasks}
          tasks={isGoalOptionSelected ? goals : tasks}
          tasksDispatch={isGoalOptionSelected ? goalsDispatch : tasksDispatch}
          getData={getData}
          isMSTeamsView
          {...duration}
        />

        <TeamTasksCardView
          setTeamCount={setTeamCount}
          tagId={team.id}
          tagType={SEARCH_TAG_TYPES.team}
          style={cardViewStyles}
          isSectionVisible={TEAM_TASKS_VIEW_TYPES.card === teamTasksViewType}
          activeFilters={activeFilters}
          updateTasksLoading={updateTasksLoading}
          updateIsTasksDataChanged={updateIsCardViewDataChanged}
          hasTableViewDataChanged={tasksDataChanged[TEAM_TASKS_VIEW_TYPES.list]}
          refreshData={refreshData}
          selectedTeam={team}
          type={selectedTaskType}
          {...duration}
          showListView={showListView}
        />
      </ViewsContainer>
      <CustomBackdrop />
    </Container>
  ) : (
    <Skeleton height="30vh" />
  );
}

export default withRouter(MSTeamHubView);

const StyledViewTypes = styled(ViewTypes)`
  margin-right: 1rem;
`;

const HeadingWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const CreateButton = styled(StyledButton)`
  padding: 8px 40px;
  border-color: ${(props) => props.theme.palette.primary.main};
`;

const ViewsContainer = styled.div`
  margin-top: 2rem;
`;
